/**
 * <TutorStudentTile> — un élève du live dans la pellicule du masteur : sa vidéo, et son mode dessous
 * (molecule).
 *
 * La pellicule du round 2 (Wajih, 02/11) remplace la carte élève : sa vidéo (`<TutorStudentVideo>` : son
 * prénom dessus, **une seule puce**, la plus urgente, le liseré vert pendant qu'il parle), et
 * Dialogue · Concentré dessous, sur toute la largeur. L'élève regardé est entouré de l'univers et
 * s'ouvre sur place : `children` (sa liste d'activités) se pose sous sa bascule, dans le même cadre.
 * Round 4 (Wajih : « on ne voit pas que le tout forme un seul bloc ») : chaque élève est une carte — sa
 * vidéo, sa bascule et son activité —, regardé ou non ; le regardé prend le liseré de l'univers.
 *
 * Toucher la vidéo regarde l'élève (`onSelect`) : sa liste vient sous sa vidéo, et ce qu'il fait
 * s'ouvre au centre. Contrôlée : le mode et la sélection arrivent en props.
 *
 * Round 3 (Wajih) : un élève qu'on ne regarde pas n'a pas de liste, mais **une petite barre : l'activité
 * qu'il a devant lui, son visage posé dessus**, à la couleur de sa matière ; la toucher l'ouvre au centre
 * (`onOpenActivity`).
 *
 * Weekly du 03/11 (W5, W9) : une carte plus épurée.
 *  - **Dialogue · Concentré** n'est plus une bande sous la vidéo : un bouton incrusté en bas à droite de la
 *    visio (`<TutorSoundModeToggle>`), qui dit le mode et tourne pendant la bascule.
 *  - **La pastille rouge**, en haut à droite de la carte, dit qu'il y a quelque chose à aller voir chez lui
 *    (`tutorStudentAttention` : une carte au bac « À décider », ou des messages non lus). Sa main levée, son
 *    rendu, son silence ou sa sortie de l'appli y passent par leur carte au bac, jamais en double.
 *    Elle remplace le compte « à décider » et la bulle des messages posés sur la vidéo ; le détail est dans
 *    le fil, et le lecteur d'écran l'entend sur la carte. Des messages attendent : elle ouvre son fil du
 *    chat (`onOpenChat`), le geste de la bulle d'avant.
 *  - Sur la vidéo, il ne reste que son chrono (`<TutorStudentChips>`), jamais recouvert.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UISoundMode } from "../../../ports/primary/ui/types/studentLive"
import { type UITutorLiveStudent } from "../../../ports/primary/ui/types/tutorLive"
import { NotificationDot } from "../../atoms/NotificationDot"
import { slopTo } from "../../utils/touchTarget"
import { tutorStudentAttention } from "../TutorStudentChips"
import { TutorSoundModeToggle } from "../TutorSoundModeToggle"
import { TutorStudentActivityBar } from "../TutorStudentActivityBar"
import { TutorStudentVideo } from "../TutorStudentVideo"

export type TutorStudentTileProps = {
    student: UITutorLiveStudent
    /**
     * Le rapport largeur / hauteur de sa vidéo : la pellicule le choisit selon la place. Absent : la carte
     * remplit ce qui la porte et sa vidéo prend ce qui reste, en grand (une case de la mosaïque).
     */
    videoAspect?: number
    /** C'est lui qu'on regarde : le cadre de l'univers. Défaut `false`. */
    selected?: boolean
    onSelect: () => void
    onChangeMode: (mode: UISoundMode) => void
    /** La bascule du mode part. Défaut `false`. */
    modePending?: boolean
    /** Sa vraie caméra (`VideoView` de LiveKit). Absente : sa photo, ou son visage caméra coupée. */
    video?: React.ReactNode
    /** Ce qui se pose sous sa bascule : la liste d'activités de l'élève regardé. */
    children?: React.ReactNode
    /** Sa petite barre (un élève qu'on ne regarde pas) : ouvrir au centre l'activité qu'il a devant lui. */
    onOpenActivity?: (activityId: string) => void
    /** Ses messages non lus : la pastille ouvre son fil du chat. Absent : la pastille ne se touche pas. */
    onOpenChat?: () => void
    testID?: string
}

export function TutorStudentTile(props: TutorStudentTileProps) {
    const {
        student,
        videoAspect,
        selected = false,
        onSelect,
        onChangeMode,
        modePending = false,
        video,
        children,
        onOpenActivity,
        onOpenChat,
        testID,
    } = props
    const attention = tutorStudentAttention(student)
    const chat = (student.unread ?? 0) > 0 ? onOpenChat : undefined

    const fill = videoAspect == null
    return (
        <View style={[styles.root(selected), fill && styles.fill]} testID={testID}>
            <View style={fill ? styles.videoFill : styles.video(videoAspect)}>
                <Pressable
                    onPress={onSelect}
                    accessibilityRole="button"
                    accessibilityState={{ selected }}
                    accessibilityLabel={[TEXTS.look(student.firstname), ...attention].join(", ")}
                    style={styles.press}
                >
                    <TutorStudentVideo student={student} size={fill ? "stage" : undefined} video={video} />
                </Pressable>
                {/* Posé sur la vidéo, hors du bouton qui la regarde : un toucher bascule le mode, pas la carte. */}
                <View style={styles.mode(fill, fill ? MODE_SIZE.fill : MODE_SIZE.tile)}>
                    <TutorSoundModeToggle
                        mode={student.soundMode}
                        onChange={onChangeMode}
                        pending={modePending}
                        disabled={student.presence !== "here"}
                        studentName={student.firstname}
                        size={fill ? MODE_SIZE.fill : MODE_SIZE.tile}
                    />
                </View>
            </View>
            {!selected && <TutorStudentActivityBar student={student} onOpenActivity={onOpenActivity} />}
            {children}
            {attention.length > 0 && (
                // Des messages attendent : la pastille ouvre son fil, d'un geste, comme la bulle d'avant ; sinon elle
                // laisse passer le toucher.
                <Pressable
                    onPress={chat}
                    disabled={chat == null}
                    pointerEvents={chat == null ? "none" : "auto"}
                    accessibilityRole={chat != null ? "button" : undefined}
                    accessibilityLabel={chat != null ? TEXTS.chat(student.firstname) : undefined}
                    hitSlop={chat != null ? DOT_SLOP : undefined}
                    style={styles.dot}
                >
                    <NotificationDot size={DOT_SIZE} />
                </Pressable>
            )}
        </View>
    )
}

const TEXTS = {
    look: (firstname: string) => `Regarder ${firstname}`,
    chat: (firstname: string) => `Les messages de ${firstname}`,
}

/** Le bouton de mode : une vignette de la pellicule, une case de la mosaïque. */
const MODE_SIZE = { tile: 32, fill: 40 } as const

const DOT_SIZE = 14
/** Une pastille à toucher : la cible s'étend à celle d'un doigt. */
const DOT_SLOP = slopTo(DOT_SIZE)

const styles = StyleSheet.create((theme) => ({
    /** Un élève, un seul bloc (round 4) : sa carte, entourée de l'univers quand on le regarde. */
    root: (selected: boolean) => ({
        gap: theme.spacing["1"],
        padding: theme.spacing["2"],
        borderRadius: theme.radius.lg,
        borderWidth: 2,
        borderColor: selected ? theme.colors.role : theme.colors["border-subtle"],
        backgroundColor: theme.colors["surface-raised"],
    }),
    fill: { flex: 1, minHeight: 0 },
    video: (aspect: number) => ({ aspectRatio: aspect }),
    videoFill: { flex: 1, minHeight: 0, borderRadius: theme.radius.lg, overflow: "hidden" as const },
    press: { flex: 1, minHeight: 0 },
    /** Le mode, au coin bas droit de la vidéo, à l'écart du bord comme le chrono l'est du sien. */
    /**
     * Le bouton de mode, au coin bas droit de la vidéo. Son conteneur s'élargit de la marge qui porte la
     * cible à celle d'un doigt (une zone de toucher ne déborde jamais de son parent), et recule d'autant :
     * le bouton reste à sa place.
     */
    mode: (fill: boolean, size: number) => ({
        position: "absolute" as const,
        padding: slopTo(size),
        right: theme.spacing[fill ? "3" : "1"] - slopTo(size),
        bottom: theme.spacing[fill ? "3" : "1"] - slopTo(size),
    }),
    /** Il y a quelque chose à aller voir : le coin haut droit de la carte, à cheval sur son bord. */
    dot: { position: "absolute" as const, top: -theme.spacing["1"], right: -theme.spacing["1"] },
}))
